import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ExternalLink, Users, Trash2, Eye, Upload, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { format } from "date-fns";
import PublishDialog from "@/components/PublishDialog";

export default function Dashboard() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [publishEvent, setPublishEvent] = useState(null);

  const pro = user?.pro_until && new Date(user.pro_until) > new Date();
  const proExpiresIn = pro ? Math.ceil((new Date(user.pro_until) - new Date()) / 86400000) : 0;

  const load = async () => {
    try {
      const { data } = await api.get("/events");
      setEvents(data);
      const entries = await Promise.all(
        data.map(async (e) => {
          try { const r = await api.get(`/events/${e.id}/rsvps`); return [e.id, r.data]; }
          catch { return [e.id, []]; }
        })
      );
      const map = {};
      entries.forEach(([id, list]) => {
        const yes = list.filter((r) => r.attending === "yes").reduce((s, r) => s + (r.guest_count || 1), 0);
        map[id] = { total: list.length, yes };
      });
      setCounts(map);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this invite and all its RSVPs?")) return;
    try { await api.delete(`/events/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F4]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Host studio</div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl mt-2 font-light tracking-tight">
              Hello, {user?.name || "friend"}.
            </h1>
            {pro && (
              <div data-testid="dashboard-pro-status" className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-neutral-900 text-white text-[10px] tracking-[0.25em] uppercase">
                Pro · {proExpiresIn} day{proExpiresIn === 1 ? "" : "s"} remaining
              </div>
            )}
          </div>
          <Link to="/create">
            <Button data-testid="dashboard-create" className="rounded-sm bg-neutral-900 hover:bg-neutral-800 h-11 tracking-[0.2em] uppercase text-xs px-6">
              <Plus size={16} className="mr-2" /> New Invite
            </Button>
          </Link>
        </div>

        <div className="mt-12">
          {loading ? (
            <div className="text-neutral-500">Loading…</div>
          ) : events.length === 0 ? (
            <div className="border border-dashed border-black/20 p-16 text-center">
              <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl font-light">No invites yet.</div>
              <p className="text-neutral-600 mt-2">Create your first soirée — previewing is always free.</p>
              <Link to="/create"><Button data-testid="empty-create" className="mt-6 rounded-sm bg-neutral-900 hover:bg-neutral-800">Create Invite</Button></Link>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-6">
              {events.map((e) => {
                const c = counts[e.id] || { total: 0, yes: 0 };
                const isPublished = e.status === "published";
                const isOwner = e.owner_id === user?.id;
                let when = "";
                try { when = format(new Date(e.event_datetime), "EEE, MMM d · p"); } catch {}
                return (
                  <div key={e.id} data-testid={`event-card-${e.id}`} className="col-span-12 md:col-span-6 xl:col-span-4 border border-black/10 bg-white p-7 hover:shadow-xl transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">{(e.event_type || "").replace("_", " ")}</div>
                        <span
                          data-testid={`status-${e.id}`}
                          className={`text-[9px] tracking-[0.25em] uppercase px-2 py-0.5 ${isPublished ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}`}
                        >
                          {isPublished ? "Published" : "Draft"}
                        </span>
                        {!isOwner && (
                          <span data-testid={`role-${e.id}`} className="text-[9px] tracking-[0.25em] uppercase px-2 py-0.5 bg-neutral-100 text-neutral-700">
                            Co-host
                          </span>
                        )}
                      </div>
                      {isOwner && (
                        <button onClick={() => remove(e.id)} data-testid={`delete-event-${e.id}`} className="text-neutral-400 hover:text-red-600">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    <h3 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl mt-3 font-normal leading-tight">{e.title}</h3>
                    <div className="mt-3 text-sm text-neutral-600">{when}</div>
                    <div className="mt-1 text-sm text-neutral-500 truncate">{e.location_name}</div>
                    <div className="mt-6 flex items-center gap-6 pt-4 border-t border-black/5">
                      <div className="flex items-center gap-2 text-sm"><Users size={14} /> <span className="tabular-nums">{c.total}</span> <span className="text-neutral-500">RSVPs</span></div>
                      <div className="text-sm"><span className="tabular-nums">{c.yes}</span> <span className="text-neutral-500">attending</span></div>
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {isPublished ? (
                        <>
                          <Link to={`/events/${e.id}`} className="flex-1 min-w-[120px]">
                            <Button variant="outline" data-testid={`track-${e.id}`} className="w-full rounded-sm">Track RSVPs</Button>
                          </Link>
                          <Link to={`/events/${e.id}/edit`}>
                            <Button variant="ghost" data-testid={`edit-${e.id}`} className="rounded-sm" title="Edit invite"><Pencil size={14} /></Button>
                          </Link>
                          <a href={`/i/${e.slug}`} target="_blank" rel="noreferrer">
                            <Button variant="ghost" data-testid={`view-${e.id}`} className="rounded-sm"><ExternalLink size={16} /></Button>
                          </a>
                        </>
                      ) : (
                        <>
                          <Link to={`/preview/${e.id}`} className="flex-1 min-w-[120px]">
                            <Button variant="outline" data-testid={`preview-${e.id}`} className="w-full rounded-sm"><Eye size={14} className="mr-2" /> Preview</Button>
                          </Link>
                          <Link to={`/events/${e.id}/edit`}>
                            <Button variant="ghost" data-testid={`edit-${e.id}`} className="rounded-sm" title="Edit invite"><Pencil size={14} /></Button>
                          </Link>
                          <Button
                            onClick={() => setPublishEvent(e)}
                            data-testid={`publish-${e.id}`}
                            className="rounded-sm bg-neutral-900 hover:bg-neutral-800 flex-1 min-w-[120px]"
                          >
                            <Upload size={14} className="mr-2" /> Publish
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <PublishDialog
        open={!!publishEvent}
        onOpenChange={(v) => !v && setPublishEvent(null)}
        event={publishEvent}
        onPublished={load}
      />
    </div>
  );
}
